import { isDesignMode } from "../../../config/designMode";
import { API_BASE_URL } from "../../../config/env";
import { useUserStore } from "../../../store/userStore";

export type ObjectiveChangedAction = "created" | "updated" | "deleted";

export type ObjectiveStreamEvent =
    | { type: "connected" }
    | { type: "heartbeat" }
    | {
          type: "objective_changed";
          action: ObjectiveChangedAction;
          objective_id: string;
          title?: string;
          actor_user_id?: string;
          timestamp?: string;
      }
    | {
          type: "objective_movements_ready";
          objective_id: string;
          title?: string;
          actor_user_id?: string;
          timestamp?: string;
      };

export interface ObjectiveEventsStreamOptions {
    signal?: AbortSignal;
    onEvent?: (event: ObjectiveStreamEvent) => void;
    onError?: (error: Error) => void;
}

function parseSseChunk(buffer: string): { events: ObjectiveStreamEvent[]; rest: string } {
    const events: ObjectiveStreamEvent[] = [];
    const parts = buffer.split("\n\n");
    const rest = parts.pop() ?? "";

    for (const part of parts) {
        const line = part.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        const jsonStr = line.slice(5).trim();
        if (!jsonStr) continue;
        try {
            events.push(JSON.parse(jsonStr) as ObjectiveStreamEvent);
        } catch {
            // ignore malformed frames
        }
    }

    return { events, rest };
}

/**
 * Long-lived SSE subscription to `/objectives/events/stream`.
 * Resolves when the stream ends or the signal is aborted.
 */
export async function subscribeObjectiveEventsStream(
    options: ObjectiveEventsStreamOptions = {},
): Promise<void> {
    if (isDesignMode) {
        return;
    }

    const token = useUserStore.getState().tokens.backendToken;
    if (!token) {
        throw new Error("Token de autenticação ausente.");
    }

    const response = await fetch(`${API_BASE_URL}/objectives/events/stream`, {
        method: "GET",
        headers: {
            Accept: "text/event-stream",
            Authorization: `Bearer ${token}`,
        },
        signal: options.signal,
    });

    if (!response.ok) {
        const text = await response.text();
        const error = new Error(`Erro ${response.status}: ${text}`);
        options.onError?.(error);
        throw error;
    }

    const reader = response.body?.getReader();
    if (!reader) {
        const error = new Error("Resposta sem corpo para streaming.");
        options.onError?.(error);
        throw error;
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
        while (true) {
            const { value, done } = await reader.read();

            if (value) {
                buffer += decoder.decode(value, { stream: true });
                const parsed = parseSseChunk(buffer);
                buffer = parsed.rest;
                for (const event of parsed.events) {
                    options.onEvent?.(event);
                }
            }

            if (done) {
                buffer += decoder.decode();
                const parsed = parseSseChunk(buffer.trim() ? `${buffer}\n\n` : buffer);
                for (const event of parsed.events) {
                    options.onEvent?.(event);
                }
                break;
            }
        }
    } catch (error) {
        if (options.signal?.aborted) {
            return;
        }
        const err = error instanceof Error ? error : new Error(String(error));
        options.onError?.(err);
        throw err;
    }
}
